export interface TenantIdentity {
  name: string;
  tagline: string;
  logoSvgPath: string;
  logoUrl: string;
  footerText: string;
  disclaimerText: string;
}

export interface TenantLanding {
  headline: string;
  headlineAccent: string;
  subheadline: string;
  description: string;
  ctaPrimary: string;
  ctaSecondary: string;
}

export interface TenantTheme {
  primaryColorHex: string;
}

export interface TenantLinks {
  website: string;
  backoffice: string;
  telegram: string;
  whatsapp: string;
  facebook: string;
  discord: string;
  certikAudit: string;
}

export interface TenantContracts {
  slgns: string;
  lgns: string;
  dai: string;
  usdt: string;
  stakingHelper: string;
  staking: string;
  lp: string;
  treasury: string;
  distributor: string;
  warmup: string;
}

export interface TenantWallets {
  payment: string;
  admin: string[];
}

export interface TenantYield {
  rebaseYield: number;
  rebasesPerDay: number;
  overrideBonus: number;
  conservativeRate: number;
  optimisticRate: number;
  overrideTiers: ReadonlyArray<{ personals: number; mainUSD: number; pct: number }>;
}

export interface TenantTrust {
  certikScore: number;
  treasuryBacking: string;
}

export interface TenantChain {
  chainId: number;
  moralisName: string;
  alchemySubdomain: string;
  rpcUrl?: string;
}

/** Phase of a feature's migration to the Anubis chain. */
export type MigrationPhase = 'active' | 'migrating' | 'moved';

/**
 * Anubis migration state. Spider Web (team overrides) and DAO Rewards are
 * moving to the Anubis chain for settlement. Each feature advances through
 * active → migrating → moved independently, driven purely by config (no code
 * deploy needed to flip phases):
 *   - active:    normal behavior, settled on Polygon (today)
 *   - migrating: still visible, but Polygon-derived numbers are labeled
 *                "reference only — settling on Anubis" + a link to Anubis
 *   - moved:     feature replaced with a handoff card pointing to Anubis
 */
export interface AnubisMigration {
  spiderWebPhase: MigrationPhase;
  rewardsHubPhase: MigrationPhase;
  anubisUrl: string;
}

export interface TenantFeatures {
  aiAdvisor: boolean;
  spiderWeb: boolean;
  communityChannels: boolean;
  betaGate: boolean;
  marketingMaterials: boolean;
  calculator: boolean;
  yieldTools: boolean;
  aiVoiceInput: boolean;
  aiVoiceOutput: boolean;
  aiStreaming: boolean;
  rewardsHub: boolean;
  analytics: boolean;
  balanceHistory: boolean;
  explainerVideos: boolean;
  projections: boolean;
  activity: boolean;
  protocolOverview: boolean;
}

export interface TenantSubscription {
  monthlyPriceUSD: number;
  lifetimePriceUSD: number;
  annualPriceUSD: number;
  introPriceUSD: number;
  gracePeriodDays: number;
  acceptedTokens: readonly string[];
}

/**
 * Protocol/chain-shape descriptor. Lets a tenant run a different staked-token
 * model and price source without code branches at every call site.
 * - rebasing: sOHM-style sLGNS — staked balance IS the LGNS amount, read 1:1.
 *             Used by BOTH Polygon and Anubis (Anubis sLGNS = 0x2243…845B8C, 9-dec).
 * - gOHM:     gOHM-style index-wrapped staked token — LGNS_equivalent = float × index.
 *             Supported but no tenant currently uses it. (gLGNS was mistaken for this;
 *             it is actually a fixed-supply governance token, not the staked receipt.)
 * `contracts.slgns` holds the staked-token address generically.
 */
export interface TenantProtocol {
  stakedTokenModel: 'rebasing' | 'gOHM';
  stakedTokenDecimals: number;        // 9 for sLGNS, 18 for gLGNS
  stakedTokenSymbol: string;          // display label, e.g. 'sLGNS' | 'gLGNS'
  priceSource: 'dexscreener' | 'lp';  // 'lp' = derive price from the LP reserves
  // Some forks (e.g. Anubis) expose no staking.epoch() getter; instead they
  // emit a rebase event whose 3rd data word is the per-epoch `distribute`.
  // When set, the rebase-rate reader uses the latest such event (on
  // contracts.staking) as the distribute source. Polygon leaves this unset
  // and keeps the epoch() path.
  rebaseEventTopic?: string;
}

export interface TenantConfig {
  identity: TenantIdentity;
  landing: TenantLanding;
  theme: TenantTheme;
  links: TenantLinks;
  contracts: TenantContracts;
  wallets: TenantWallets;
  yield: TenantYield;
  trust: TenantTrust;
  chain: TenantChain;
  features: TenantFeatures;
  subscription: TenantSubscription;
  launchDate: string;
  anubisMigration: AnubisMigration;
  protocol: TenantProtocol;
}

export const CHAIN_MORALIS_NAMES: Record<number, string> = {
  137: 'polygon',
  1: 'eth',
  56: 'bsc',
  43114: 'avalanche',
  250: 'fantom',
  42161: 'arbitrum',
  10: 'optimism',
  6714: 'anubis', // Anubis chain — not a Moralis network; reads go via chain.rpcUrl
};

export const CHAIN_ALCHEMY_SUBDOMAINS: Record<number, string> = {
  137: 'polygon-mainnet',
  1: 'eth-mainnet',
  42161: 'arb-mainnet',
  10: 'opt-mainnet',
  6714: 'anubis', // placeholder — Anubis is not an Alchemy network; reads use chain.rpcUrl
};

const DEFAULT_OVERRIDE_TIERS: TenantYield['overrideTiers'] = [
  { personals: 1,  mainUSD: 100,   pct: 16 },
  { personals: 2,  mainUSD: 300,   pct: 10 },
  { personals: 3,  mainUSD: 500,   pct: 8  },
  { personals: 4,  mainUSD: 800,   pct: 7  },
  { personals: 5,  mainUSD: 1200,  pct: 6  },
  { personals: 6,  mainUSD: 1700,  pct: 6  },
  { personals: 7,  mainUSD: 2300,  pct: 6  },
  { personals: 8,  mainUSD: 3000,  pct: 6  },
  { personals: 9,  mainUSD: 3700,  pct: 5  },
  { personals: 10, mainUSD: 4600,  pct: 5  },
  { personals: 11, mainUSD: 5000,  pct: 5  },
  { personals: 12, mainUSD: 7000,  pct: 5  },
  { personals: 13, mainUSD: 8000,  pct: 5  },
  { personals: 14, mainUSD: 9000,  pct: 5  },
  { personals: 15, mainUSD: 10000, pct: 5  },
];

export const DEFAULT_TENANT_CONFIG: TenantConfig = {
  identity: {
    name: 'Origin Sovereign',
    tagline: 'Multi-Wallet Yield Strategy on Polygon',
    logoSvgPath: 'M12 2L3 7v5c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7L12 2z',
    logoUrl: '',
    footerText: 'Portfolio management and yield intelligence for the LGNS ecosystem.',
    disclaimerText: 'Cryptocurrency investments carry risk. Past performance is not indicative of future results. Always conduct your own research and consult with a financial advisor before making investment decisions. Origin Sovereign is a portfolio tracking platform and does not provide financial advice.',
  },
  landing: {
    headline: 'The Dashboard Every',
    headlineAccent: 'sLGNS Staker',
    subheadline: 'Needs.',
    description: 'Real-time yield tracking, compound projections, and AI-powered advisory \u2014 built for the LGNS ecosystem.',
    ctaPrimary: 'Get Started Free',
    ctaSecondary: 'View Dashboard',
  },
  theme: {
    primaryColorHex: '#84cc16',
  },
  links: {
    website: 'https://origindefi.io',
    backoffice: 'https://originworld.org/',
    telegram: 'https://t.me/+e6k9A-lklg0yN2Fh',
    whatsapp: 'https://chat.whatsapp.com/ETuC8SsXTrR0OyvBg4irfq',
    facebook: 'https://www.facebook.com/groups/1742008703026076',
    discord: '',
    certikAudit: 'https://skynet.certik.com/projects/origin#pulseFeed',
  },
  contracts: {
    slgns: '0x99a57E6C8558BC6689f894e068733ADf83C19725',
    lgns: '0xeb51d9a39ad5eef215dc0bf39a8821ff804a0f01',
    dai: '0x8f3Cf7ad23Cd3CaDbD9735AFf958023239c6A063',
    usdt: '0xc2132D05D31c914a87C6611C10748AEb04B58e8F',
    stakingHelper: '0x8e9cf7c537b6a05e3e97520e76f0e79e3674eead',
    staking: '0x1964ca90474b11ffd08af387b110ba6c96251bfc',
    lp: '0x882df4B0fB50a229C3B4124EB18c759911485bFb',
    treasury: '0x7B9B7d4F870A38e92c9a181B00f9b33cc8Ef5321',
    distributor: '0x8DEa9182ca68E101C4B351E4601f56044a5Dd611',
    warmup: '0x81e77a6ceed59000ba5136eb735d18fd9bf5021e',
  },
  wallets: {
    payment: '',
    admin: [],
  },
  yield: {
    rebaseYield: 0.002018,
    // Polygon epochs are BLOCK-based: 14,400 blocks/epoch (verified on-chain via
    // endBlock delta). At the current ~1.5s Polygon block time that's a 6h epoch
    // = 4 rebases/day. This is block-time-dependent: if Polygon block time
    // changes, recompute rebasesPerDay = 86400 / (14400 * blockTimeSeconds).
    // (The per-rebase yield itself is read live from the contract and self-
    // corrects; only this daily multiplier is a manual knob.)
    rebasesPerDay: 4,
    overrideBonus: 0.16,
    conservativeRate: 0.004,
    optimisticRate: 0.008,
    overrideTiers: DEFAULT_OVERRIDE_TIERS,
  },
  trust: {
    certikScore: 85,
    treasuryBacking: '≥1 DAI per token',
  },
  chain: {
    chainId: 137,
    moralisName: 'polygon',
    alchemySubdomain: 'polygon-mainnet',
  },
  features: {
    aiAdvisor: true,
    spiderWeb: true,
    communityChannels: true,
    betaGate: true,
    marketingMaterials: true,
    calculator: true,
    yieldTools: true,
    aiVoiceInput: true,
    aiVoiceOutput: true,
    aiStreaming: true,
    rewardsHub: true,
    analytics: true,
    balanceHistory: true,
    explainerVideos: true,
    projections: true,
    activity: true,
    protocolOverview: true,
  },
  subscription: {
    monthlyPriceUSD: 10,
    lifetimePriceUSD: 150,
    annualPriceUSD: 84,
    introPriceUSD: 5,
    gracePeriodDays: 5,
    acceptedTokens: ['LGNS', 'DAI', 'USDT'],
  },
  launchDate: '2026-03-30T13:00:00Z',
  anubisMigration: {
    spiderWebPhase: 'active',
    rewardsHubPhase: 'active',
    anubisUrl: 'https://anubis.origindefi.io',
  },
  // Polygon defaults: sLGNS rebasing model, DexScreener pricing. The Anubis
  // tenant overrides this to { 'gOHM', 18, 'gLGNS', 'lp' } in its DB config.
  protocol: {
    stakedTokenModel: 'rebasing',
    stakedTokenDecimals: 9,
    stakedTokenSymbol: 'sLGNS',
    priceSource: 'dexscreener',
  },
};

export function deepMerge<T extends Record<string, unknown>>(base: T, overlay: Partial<T>): T {
  const result = { ...base };
  for (const key of Object.keys(overlay) as Array<keyof T>) {
    const val = overlay[key];
    if (val !== undefined && val !== null) {
      if (typeof val === 'object' && !Array.isArray(val) && typeof result[key] === 'object' && !Array.isArray(result[key])) {
        result[key] = deepMerge(result[key] as Record<string, unknown>, val as Record<string, unknown>) as T[keyof T];
      } else {
        result[key] = val as T[keyof T];
      }
    }
  }
  return result;
}

export function resolveChainFields(config: TenantConfig): void {
  if (!config.chain.moralisName) {
    config.chain.moralisName = CHAIN_MORALIS_NAMES[config.chain.chainId] || 'polygon';
  }
  if (!config.chain.alchemySubdomain) {
    config.chain.alchemySubdomain = CHAIN_ALCHEMY_SUBDOMAINS[config.chain.chainId] || 'polygon-mainnet';
  }
}
